<template>
  <div class="compose-panel">
    <div class="compose-panel__header">
      <span class="compose-panel__title">{{ title }}</span>
      <div class="compose-panel__header-actions">
        <button v-if="composeMode === 'minimized'" class="gm-toolbar-btn" @click="$emit('maximize')" v-b-tooltip.hover :title="$t('inbox.maximize')"><b-icon icon="arrows-fullscreen" /></button>
        <button class="gm-toolbar-btn" @click="$emit('minimize')" v-b-tooltip.hover :title="$t('inbox.minimize')"><b-icon icon="dash-lg" /></button>
        <button class="gm-toolbar-btn" @click="$emit('discard')" v-b-tooltip.hover :title="$t('inbox.discardDraft')"><b-icon icon="x-lg" /></button>
      </div>
    </div>

    <b-alert variant="danger" dismissible :show="!!error" @dismissed="$emit('dismiss-error')" class="mx-3 mt-2 mb-0 py-2">{{ error }}</b-alert>

    <div class="compose-panel__fields">
      <div v-if="selectedAccount" class="cpf-row">
        <span class="cpf-label">From</span>
        <span class="cpf-from-value">{{ selectedAccount.email }}</span>
      </div>
      <div class="cpf-row">
        <span class="cpf-label">{{ $t('inbox.to') }}</span>
        <div class="cpf-input-wrap">
          <input 
            :value="to" 
            @input="$emit('update:to', $event.target.value)"
            type="email" 
            placeholder="recipient@example.com" 
            autocomplete="off" 
            class="cpf-input"
            @focus="$emit('to-focus')"
            @blur="$emit('to-blur')"
          />
          <div v-if="suggestions.length && showSuggestions" class="compose-suggestions">
            <div v-for="c in suggestions" :key="c.address" class="compose-suggestion-item" @mousedown.prevent="$emit('select-contact', c)">
              <span class="compose-suggestion-name">{{ c.name !== c.address ? c.name : '' }}</span>
              <span class="compose-suggestion-addr">{{ c.address }}</span>
            </div>
          </div>
        </div>
        <button class="cpf-cc-btn" @click="$emit('toggle-cc')">Cc</button>
      </div>
      <div class="cpf-row" v-if="showCc">
        <span class="cpf-label">{{ $t('inbox.cc') }}</span>
        <input :value="cc" @input="$emit('update:cc', $event.target.value)" type="email" placeholder="cc@example.com" class="cpf-input" style="flex:1" />
      </div>
      <div class="cpf-row">
        <span class="cpf-label">{{ $t('inbox.subject') }}</span>
        <input :value="subject" @input="$emit('update:subject', $event.target.value)" :placeholder="$t('inbox.subject')" class="cpf-input" style="flex:1" />
      </div>
    </div>

    <div class="compose-panel__toolbar">
      <button class="compose-tool" @click.prevent="$emit('exec-cmd', 'bold')" :title="$t('inbox.bold')"><b>B</b></button>
      <button class="compose-tool" @click.prevent="$emit('exec-cmd', 'italic')" :title="$t('inbox.italic')"><i>I</i></button>
      <button class="compose-tool" @click.prevent="$emit('exec-cmd', 'underline')" :title="$t('inbox.underline')"><u>U</u></button>
      <span class="compose-tool-sep" />
      <button class="compose-tool" @click.prevent="$emit('exec-cmd', 'insertUnorderedList')" :title="$t('inbox.bulletList')">•≡</button>
      <button class="compose-tool" @click.prevent="$emit('exec-cmd', 'insertOrderedList')" :title="$t('inbox.numberedList')">1≡</button>
      <span class="compose-tool-sep" />
      <button class="compose-tool" @click.prevent="$emit('insert-link')" :title="$t('inbox.insertLink')">🔗</button>
      <span class="compose-tool-sep" />
      <button class="compose-tool" @click.prevent="$refs.fileInput.click()" :title="$t('inbox.attachFile')"><b-icon icon="paperclip" /></button>
      <input ref="fileInput" type="file" multiple style="display:none" @change="onFilesSelected" />
    </div>

    <div v-if="attachments.length" class="compose-attachments">
      <div v-for="(f, i) in attachments" :key="i" class="compose-att-chip">
        <b-icon :icon="f.type.startsWith('image/') ? 'image' : 'file-earmark'" class="mr-1" />
        <span class="compose-att-name">{{ f.name }}</span>
        <span class="compose-att-size">({{ formatFileSize(f.size) }})</span>
        <button class="compose-att-remove" @click="$emit('remove-attachment', i)">&times;</button>
      </div>
    </div>

    <div
      ref="composeBody"
      class="compose-panel__body"
      contenteditable="true"
      :data-placeholder="placeholder || $t('inbox.writeMessage')"
      @input="onBodyInput"
    />

    <div class="compose-panel__footer">
      <button class="compose-send-btn" :disabled="sending" @click="$emit('send')">
        <b-spinner small v-if="sending" style="width:14px;height:14px;flex-shrink:0" />
        <svg v-else viewBox="0 0 16 16" width="14" height="14" fill="currentColor" style="flex-shrink:0"><path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"/></svg>
        {{ $t('inbox.send') }}
      </button>
      <button class="compose-discard-btn" @click="$emit('discard')">{{ $t('inbox.discard') }}</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InboxComposePanel',
  props: {
    title: { type: String, default: 'New Message' },
    to: { type: String, default: '' },
    cc: { type: String, default: '' },
    subject: { type: String, default: '' },
    bodyHtml: { type: String, default: '' },
    attachments: { type: Array, default: () => [] },
    suggestions: { type: Array, default: () => [] },
    showSuggestions: { type: Boolean, default: false },
    showCc: { type: Boolean, default: false },
    sending: { type: Boolean, default: false },
    error: { type: String, default: null },
    selectedAccount: { type: Object, default: null },
    placeholder: { type: String, default: '' },
    composeMode: { type: String, default: 'dialog' }
  },
  mounted() {
    if (this.bodyHtml && this.$refs.composeBody) {
      this.$refs.composeBody.innerHTML = this.bodyHtml;
    }
  },
  watch: {
    bodyHtml(newVal) {
      if (this.$refs.composeBody && this.$refs.composeBody.innerHTML !== newVal) {
        this.$refs.composeBody.innerHTML = newVal;
        this.$nextTick(() => this.placeCursorInReplyArea());
      }
    }
  },
  methods: {
    onBodyInput() {
      this.$emit('update:body-html', this.$refs.composeBody.innerHTML);
    },
    onFilesSelected(e) {
      const files = Array.from(e.target.files);
      this.$emit('files-selected', files);
      e.target.value = '';
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    },
    getBodyHtml() {
      return this.$refs.composeBody ? this.$refs.composeBody.innerHTML : '';
    },
    setBodyHtml(html) {
      if (this.$refs.composeBody) {
        this.$refs.composeBody.innerHTML = html;
      }
    },
    focusBody() {
      if (this.$refs.composeBody) {
        this.$refs.composeBody.focus();
      }
    },
    placeCursorInReplyArea() {
      const body = this.$refs.composeBody;
      if (!body) return;
      const replyArea = body.querySelector('.compose-reply-area');
      if (replyArea) {
        body.focus();
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(replyArea);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }
};
</script>

<style scoped>
.compose-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  border-radius: 8px 8px 0 0;
  flex-shrink: 0;
}

.compose-panel__title {
  font-size: 0.9375rem;
  font-weight: 600;
}

.compose-panel__header-actions {
  display: flex;
  gap: 0.25rem;
}

.compose-panel__header-actions .gm-toolbar-btn {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.12s;
}
.compose-panel__header-actions .gm-toolbar-btn:hover {
  background: rgba(255,255,255,0.25);
}

.compose-panel__fields {
  flex-shrink: 0;
  padding: 0.75rem 1rem 0;
}

.cpf-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.cpf-label {
  font-size: 0.875rem;
  color: var(--text-secondary, #9ca3af);
  width: 50px;
  flex-shrink: 0;
}

.cpf-input-wrap {
  flex: 1;
  position: relative;
}

.cpf-input {
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  color: var(--text-primary, #e5e7eb);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  width: 100%;
  transition: border-color 0.12s;
}
.cpf-input:focus {
  outline: none;
  border-color: var(--primary-start, #6c80ff);
}

.cpf-from-value {
  font-size: 0.875rem;
  color: var(--text-primary, #e5e7eb);
  flex: 1;
}

.cpf-cc-btn {
  background: transparent;
  border: 1px solid var(--border-separator, #2a2d35);
  color: var(--text-secondary, #9ca3af);
  border-radius: 4px;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  flex-shrink: 0;
}
.cpf-cc-btn:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  border-color: var(--primary-start, #6c80ff);
  color: var(--primary-start, #6c80ff);
}

.compose-suggestions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--bg-secondary, #1e1f26);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
}

.compose-suggestion-item {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.12s;
}
.compose-suggestion-item:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
}

.compose-suggestion-name {
  font-weight: 500;
  color: var(--text-primary, #e5e7eb);
  white-space: nowrap;
}

.compose-suggestion-addr {
  color: var(--text-muted, #6b7280);
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.compose-panel__toolbar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-separator, #2a2d35);
  flex-shrink: 0;
}

.compose-tool {
  background: transparent;
  border: none;
  color: var(--text-secondary, #9ca3af);
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.12s, color 0.12s;
}
.compose-tool:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  color: var(--text-primary, #e5e7eb);
}

.compose-tool-sep {
  width: 1px;
  height: 20px;
  background: var(--border-separator, #2a2d35);
  margin: 0 0.25rem;
}

.compose-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-bottom: 1px solid var(--border-separator, #2a2d35);
  flex-shrink: 0;
}

.compose-att-chip {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--bg-primary, #16171d);
  border: 1px solid var(--border-separator, #2a2d35);
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-primary, #e5e7eb);
}

.compose-att-name {
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.compose-att-size {
  color: var(--text-muted, #6b7280);
  font-size: 0.75rem;
}

.compose-att-remove {
  background: none;
  border: none;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  padding: 0;
  margin-left: 0.25rem;
  transition: color 0.12s;
}
.compose-att-remove:hover {
  color: #ef4444;
}

.compose-panel__body {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-primary, #e5e7eb);
  outline: none;
  min-height: 200px;
}
.compose-panel__body:empty:before {
  content: attr(data-placeholder);
  color: var(--text-muted, #6b7280);
  pointer-events: none;
}

.compose-panel__footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border-separator, #2a2d35);
  flex-shrink: 0;
}

.compose-send-btn {
  background: linear-gradient(135deg, var(--primary-start, #6c80ff) 0%, var(--primary-end, #764ba2) 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.12s, box-shadow 0.12s;
}
.compose-send-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}
.compose-send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.compose-discard-btn {
  background: transparent;
  border: 1px solid var(--border-separator, #2a2d35);
  color: var(--text-secondary, #9ca3af);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.compose-discard-btn:hover {
  background: var(--hover-bg, rgba(255,255,255,0.04));
  border-color: #ef4444;
  color: #ef4444;
}
</style>
